import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { FormApi } from 'final-form';
import * as yup from 'yup';
import { observer } from 'mobx-react';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import downloadReceipt from 'utils/downloadReceipt';
import Loader from 'ui/Loader/Loader';
import Form from 'ui/Form/Form';
import Button from 'ui/Button/Button';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { StatusModal, OTPConfirmationModal } from 'components/modals';
import { AxiosStatus } from 'api/axiosConfig';
import { requiredOnly, validationCallback } from 'utils/validation';
import validateOtpPin from 'utils/otpValidation';
import { NETWORK_REQUIRED } from 'utils/constants';
import { handlePinError } from 'utils/pinValidation';
import { changeFieldValue } from 'utils/formMutators';
import { ITransactionField } from 'ui/structure/TransactionCard';
import NavigateBackStep from 'ui/navigateBackStep/NavigateBackStep';
import routePaths from 'routes/routePaths';
import accountsStore from 'store/accountsStore';
import api from 'api/api';
import { onlyNumbersWithDecimals } from 'utils/formatters';
import { IApiResponse } from 'utils/models';

import { IOTPError } from 'components/authModules/LogIn/login.model';
import renderSSNITFields from './SSNITFields';
import {
  ISSNITPaymentsState,
  IFormValues,
  Steps,
  IResponseVerifyData,
  IGetVerifyResponse,
  IPaySSNIT,
} from './SSNIT.model';

import styles from './SSNIT.module.css';

const confirmOrder = [
  'Payment Advice Number:',
  'Customer Name:',
  'Customer Code:',
  'Contribuion Type:',
  'Name of Establishment:',
  'Receipt ID:',
  'Payment Advice Reference:',
  'Payment Advice Date:',
  'Source Account:',
  'Total Contribution Amount:',
  'Total Penalty Amount:',
  'Total Amount:',
];

const transactionDataConfig = {
  adviceNumber: 'Payment Advice Number:',
  customerName: 'Customer Name:',
  customerCode: 'Customer Code:',
  contributionType: 'Contribution Type:',
  establishmentName: 'Name of Establishment:',
  receiptId: 'Receipt ID:',
  adviceReference: 'Payment Advice Reference:',
  adviceDate: 'Payment Advice Date:',
  sourceAccount: 'Source Account:',
  contributionAmount: 'Total Contribution Amount:',
  penaltyAmount: 'Total Penalty Amount:',
  payAmount: 'Amount:',
};

const SSNIT = () => {
  const [state, setState] = useState<ISSNITPaymentsState>({
    accounts: [],
    currency: 'GHS',
  });
  const updateState = useCallback((update: Partial<ISSNITPaymentsState>) => {
    setState((prevState) => ({ ...prevState, ...update }));
  }, []);
  const [step, setStep] = useState(Steps.VERIFY);

  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [loadingData, setLoadingData] = useState(false);
  const [transactionId, setTransactionId] = useState<number>(NaN);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string>('');
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [confirmData, setConfirmData] = useState<ITransactionField[]>([]);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;
  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const dynamicSchema = useCallback((currentStep) => {
    if (currentStep === Steps.VERIFY) {
      return yup.object().shape({
        adviceNumber: requiredOnly('Payment advice number'),
        customerName: requiredOnly('Customer name'),
        customerCode: requiredOnly('Customer code'),
        contributionType: requiredOnly('Contribution Type'),
      });
    }
    if (currentStep === Steps.FIRST_PROCEED) {
      return yup.object().shape({
        adviceNumber: requiredOnly('Payment advice number'),
        customerName: requiredOnly('Customer name'),
        customerCode: requiredOnly('Customer code'),
        contributionType: requiredOnly('Contribution Type'),
        contributionAmount: requiredOnly('Total contribution amount'),
        penaltyAmount: requiredOnly('Total penalty amount'),
        payAmount: requiredOnly('Total Amount to Pay'),
        establishmentName: requiredOnly('Name of establishment'),
        receiptId: requiredOnly('Receipt ID'),
        adviceReference: requiredOnly('Advice Reference'),
      });
    }
    return yup.object().shape({
      adviceNumber: requiredOnly('Payment advice number'),
      customerName: requiredOnly('Customer name'),
      customerCode: requiredOnly('Customer code'),
      contributionType: requiredOnly('Contribution Type'),
      contributionAmount: requiredOnly('Total contribution amount'),
      penaltyAmount: requiredOnly('Total penalty amount'),
      payAmount: requiredOnly('Total Amount to Pay'),
      establishmentName: requiredOnly('Name of establishment'),
      receiptId: requiredOnly('Receipt ID'),
      adviceReference: requiredOnly('Advice Reference'),
      sourceAccount: requiredOnly('Source account'),
    });
  }, []);

  const validate = useCallback(
    (values: IFormValues) => validationCallback<any>(dynamicSchema(step))(values),
    [dynamicSchema, step],
  );

  const intialValues: IFormValues = useMemo(() => {
    const emptyValues = {
      adviceNumber: '',
      customerName: String(sessionStorage.getItem('fullName')),
      customerCode: String(sessionStorage.getItem('firstName'))[0] + String(sessionStorage.getItem('lastName')),
      contributionType: '',
      contributionAmount: '',
      penaltyAmount: '',
      payAmount: '',
      establishmentName: '',
      receiptId: '',
      adviceReference: '',
      adviceDate: '',
      sourceAccount: '',
      narration: '',
    };

    return emptyValues;
  }, []);

  const { ghsAccounts } = accountsStore;

  useEffect(() => updateState({ accounts: ghsAccounts }), [ghsAccounts, updateState]);

  useEffect(() => {
    const fetchAccounts = () => {
      if (ghsAccounts.length) {
        // accounts loaded already
        return Promise.resolve();
      }
      return accountsStore.fetchAccounts();
    };

    setLoadingData(true);

    Promise.all([fetchAccounts()]).finally(() => {
      setLoadingData(false);
    });
  }, []);

  const getReceipt = (transactionID: number) =>
    api.get(`/transaction/receipt?trnxID=${transactionID}`).then(downloadReceipt);

  const onGetReceipt = useCallback(() => getReceipt(transactionId), [transactionId]);

  const setValues = (form: FormApi<IFormValues, Partial<IFormValues>>, data: IResponseVerifyData) => {
    form.mutators.changeFieldValue('contributionAmount', onlyNumbersWithDecimals(data.totalContributionAmount));
    form.mutators.changeFieldValue('penaltyAmount', onlyNumbersWithDecimals(data.totalPenaltyAmount));
    form.mutators.changeFieldValue('payAmount', onlyNumbersWithDecimals(data.totalAmountToPay));
    form.mutators.changeFieldValue('establishmentName', data.nameOfEstablishment);
    form.mutators.changeFieldValue('receiptId', data.receiptID);
    form.mutators.changeFieldValue('adviceReference', data.paymentAdviceReference);
    form.mutators.changeFieldValue('adviceDate', data.paymentAdviceDate);
  };

  const onVerify = useCallback(
    (values: IFormValues, form: FormApi<IFormValues, Partial<IFormValues>>) => {
      if (step === Steps.VERIFY) {
        setSubmitStatus(AxiosStatus.PENDING);

        const lookupRequest = {
          paymentAdviceNumber: values.adviceNumber,
          customerName: values.customerName,
          customerCode: values.customerCode,
          contributionType: values.contributionType,
        };

        api
          .post('billspayment/lookupSSNIT', lookupRequest)
          .then((data: IGetVerifyResponse) => {
            // setSuccessMsg(data.description);
            setSubmitStatus(AxiosStatus.DEFAULT);
            setStep(Steps.FIRST_PROCEED);
            setTimeout(() => setValues(form, data.response), 0);
          })
          .catch((error: IApiResponse) => {
            console.error(error);
            setSubmitStatus(AxiosStatus.FAILURE);
            setErrorMsg(error.description);
          });
      }
    },
    [step],
  );

  const onFirstProceed = () => {
    setStep(Steps.SECOND_PROCEED);
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
  }, []);

  const initConfirm = (values: IFormValues) => {
    setSubmitStatus(AxiosStatus.DEFAULT);
    setConfirmData(getTransactionsArray(values, transactionDataConfig, undefined, confirmOrder));
    openConfirmation();
  };
  const onSubmit = useCallback((values: IFormValues) => {
    setSubmitStatus(AxiosStatus.PENDING);
    const sourceAccount = ghsAccounts.find((account) => account.accountNumber === values.sourceAccount);
    if (sourceAccount) {
      const transactionAmount = Number(values.payAmount) + Number(values.penaltyAmount);
      const availableBalance = Number(sourceAccount.availableBalance);

      if (transactionAmount > availableBalance) {
        setSubmitStatus(AxiosStatus.FAILURE);
        setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
        return;
      }
    }
    api
      .post('/customer/otp/generate')
      .then(() => {
        initConfirm(values);
        setSubmitStatus(AxiosStatus.DEFAULT);
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  }, []);

  const getButtonName = () => {
    if (step === Steps.VERIFY || step === Steps.FIRST_PROCEED) {
      return 'Proceed to Pay';
    }
    return 'Proceed';
  };

  const handlerBackClick = () => {
    setStep(Steps.VERIFY);
  };
  const onResend = () => api.post('/customer/otp/generate');
  const onConfirm = useCallback(
    (values: IFormValues, SSNITState: ISSNITPaymentsState) => (otp: string) => {
      setErrorMsg('');
      setSuccessMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      const payData: IPaySSNIT = {
        paymentAdviceNumber: values.adviceNumber,
        customerName: values.customerName,
        customerCode: values.customerCode,
        contributionType: values.contributionType.toUpperCase(),
        sourceAccount: values.sourceAccount,
        amount: Number(values.payAmount),
        currency: SSNITState.currency,
        narration: values.narration,
        passwordType: 'OTP',
        password: otp,
        transType: 'SSNIT',
      };

      return validateOtpPin(otp).then(() => {
        setSubmitStatus(AxiosStatus.PENDING);
        api
          .post('billspayment/paySSNIT', payData)
          .then((data: any) => {
            if (data.transactionId) {
              setTransactionId(data.transactionId);
            }
            setSuccessMsg('Payment is successful!');
            setSubmitStatus(AxiosStatus.SUCCESS);
          })
          .catch((err: IApiResponse) => {
            setErrorMsg(err.description);
            setSubmitStatus(AxiosStatus.FAILURE);
          })
          .catch((error: IOTPError) => {
            setOTPError(error);
            handlePinError(setSubmitStatus, setErrorMsg);
          });
      });
    },
    [],
  );

  const onBtnClick = (values, form, currentStep) => {
    if (currentStep === Steps.VERIFY) {
      onVerify(values, form);
      return;
    }
    if (currentStep === Steps.FIRST_PROCEED) {
      onFirstProceed();
      return;
    }
    onSubmit(values);
  };

  return (
    <>
      {step !== Steps.VERIFY && (
        <NavigateBackStep onClick={handlerBackClick} className={styles.mb20}>
          Back
        </NavigateBackStep>
      )}
      {loadingData && <Loader />}
      {!loadingData && (
        <Form
          onSubmit={onSubmit}
          validate={validate}
          mutators={{ changeFieldValue }}
          initialValues={intialValues}
          render={({ handleSubmit, form, values, hasValidationErrors }) => (
            <form onSubmit={handleSubmit}>
              <FieldsLeveler label="Payment details">
                {renderSSNITFields(values, form, state, submitting, step)}
              </FieldsLeveler>
              <SubmitLeveler>
                <Button
                  type={step === Steps.SECOND_PROCEED ? 'submit' : 'button'}
                  fullWidth
                  disabled={submitting || hasValidationErrors}
                  onClick={() => onBtnClick(values, form, step)}
                >
                  {submitting ? <CircularProgress size={20} color="inherit" /> : getButtonName()}
                </Button>
              </SubmitLeveler>
              <OTPConfirmationModal
                open={isConfirmationOpen}
                data={confirmData}
                onClose={closeConfirmation}
                OTPPinError={OTPError}
                onResend={onResend}
                onSubmit={onConfirm(values, state)}
                submitStatus={submitStatus}
                onSuccess={handleOnSuccess}
                onRetry={onRetryConfirm}
                getReceipt={onGetReceipt}
                successText={successMsg}
                failureText={errorMsg}
              />
            </form>
          )}
        />
      )}
      <StatusModal
        open={showStatusModal}
        status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
        onRetry={onCloseStatusModal}
        onClose={onCloseStatusModal}
        onRetryText="Close"
      >
        {submitStatus === AxiosStatus.SUCCESS ? successMsg : errorMsg}
      </StatusModal>
    </>
  );
};

export default observer(SSNIT);
